<template>
  <div>
    <mds-loader v-if="loading"></mds-loader>
    <div v-else :class="$style['hypo-portfolio-fees']">
      <component
        :is="componentType"
        v-bind="componentProps"
        @mds-section-expanded="expanded = true"
        @mds-section-closed="expanded = false"
      >
        <template v-slot:mds-section-title>
          {{ translate('fees') }}
        </template>
        <template v-slot:mds-section-supplemental-content>
          <template v-if="!expanded">
            <span :class="$style['fee-included-text']">{{ feesIncludedText }}</span>
          </template>
        </template>
        <template v-slot:default>
          <div :class="$style['fees-section']">
            <mds-layout-grid>
              <mds-row>
                <mds-col v-if="showAdvisoryFee || options.showPlanFee" :cols="6">
                  <mds-row v-if="showAdvisoryFee">
                    <mds-col :cols="12">
                      <advisory-fee
                        :mwc-id="getMwcIdForChild('advisory')"
                        :advisory-fee-setting="advisoryFeeSetting"
                        :currency-symbol="options.currencySymbol"
                        :show-annual-account-fee="options.showAnnualAccountFee"
                        :show-annual-asset-base-fee="options.showAnnualAssetBaseFee"
                        :show-annual-fee-frequency="options.showAnnualFeeFrequency"
                        :show-pay-fee-method="options.showPayFeeMethod"
                        :show-pay-fee-time="options.showPayFeeTime"
                        :show-pay-cash-first="options.showPayCashFirst"
                        :asset-based-fee-config="assetBasedFeeConfig"
                        :all-fee-schedule-list="allFeeScheduleList"
                        :environment="options.environment"
                        :user-info="userInfo"
                        @advisory-fee-setting-changed="handleAdvisoryFeeSettingChanged"
                        @alert-triggered="handleInvalidAlert"
                        @before-close="fetchLatestFeeSchedules"
                        @fee-schedules-updated="fetchLatestFeeSchedules"
                      ></advisory-fee>
                    </mds-col>
                  </mds-row>
                  <mds-row v-show="options.showPlanFee">
                    <mds-col :cols="12" :class="$style['sub-section']">
                      <plan-fee
                        :mwc-id="getMwcIdForChild('plan')"
                        :plan-fee-setting="selectPlanFeeSetting"
                        @plan-fee-changed="handlePlanFeeChanged"
                      >
                      </plan-fee>
                    </mds-col>
                  </mds-row>
                  <mds-row
                    v-if="
                      (showAdvisoryFee || options.showPlanFee) &&
                        options.showAnnuity &&
                        hasVASecurities
                    "
                  >
                    <mds-col :class="$style['sub-section']">
                      <annuity-options
                        :mwc-id="getMwcIdForChild('annuity')"
                        :annuity-setting="annuitySetting"
                        :annuity-data-prop="annuityOptionsData"
                        @annuity-updated="applyAnnuityOptionsDataToSecurityTable"
                        @include-switched-on="handleAnnuityOptionsSwitchOn"
                        @include-switched-off="clearAnnuityInSecurityTable"
                      >
                      </annuity-options>
                    </mds-col>
                  </mds-row>
                </mds-col>
                <mds-col v-if="showTransactionFee" :cols="6">
                  <transaction-fee
                    :mwc-id="getMwcIdForChild('transaction')"
                    :currency-symbol="options.currencySymbol"
                    :show-charge-for-rebalancing="options.showChargeForRebalancing"
                    :transaction-fee-setting="transactionFeeSetting"
                    :show-loi-as-front-load="options.showLoiAsFrontLoad"
                    :roaloi-data="roaLoiData"
                    :show-roa-loi="showRoaLoi"
                    @include-transaction-fee-changed="toggleTransactionFee"
                    @front-load-changed="handleFrontLoadChanged"
                    @redemption-charge-changed="applyRedemptionChargeForSecurities"
                    @charge-on-rebalancing-changed="applyChargeOnRebalancing"
                    @roa-loi-changed="handleRoaLoiFeeChanged"
                    @roa-loi-removed="handleRoaLoiFeeRemoved"
                  >
                  </transaction-fee>
                  <trading-commission
                    v-if="transactionFeeSetting && transactionFeeSetting.includeTransactionFee"
                    :mwc-id="getMwcIdForChild('trading-commission')"
                    :currency-symbol="options.currencySymbol"
                    :open="openTradingCommission"
                    :sec-with-t-c="securitiesWithTC"
                    @toggle-trading-commission="handleToggleTradingCommission"
                    @trading-commission-change="applyChangeToAllSecWithTC"
                  ></trading-commission>
                  <span
                    v-if="
                      !showHoldingLevelFees &&
                        transactionFeeSetting &&
                        transactionFeeSetting.includeTransactionFee
                    "
                    :class="$style['fee-link']"
                    @click="showStandardFeeSchedule"
                    >{{ translate('feesPortfolioScheduleLink') }}
                  </span>
                  <mds-row
                    v-if="
                      !showAdvisoryFee &&
                        !options.showPlanFee &&
                        options.showAnnuity &&
                        hasVASecurities
                    "
                  >
                    <mds-col :class="$style['sub-section']">
                      <annuity-options
                        :mwc-id="getMwcIdForChild('annuity')"
                        :annuity-setting="annuitySetting"
                        :annuity-data-prop="annuityOptionsData"
                        @annuity-updated="applyAnnuityOptionsDataToSecurityTable"
                        @include-switched-on="handleAnnuityOptionsSwitchOn"
                        @include-switched-off="clearAnnuityInSecurityTable"
                      >
                      </annuity-options>
                    </mds-col>
                  </mds-row>
                  <fee-schedule
                    :mwc-id="getMwcIdForChild('fee-schedule')"
                    :toggle="showSchedule"
                    :securities="scheduleSecurities"
                    :fee-schedule="selectedSchedule"
                    :portfolio-only="!showHoldingLevelFees"
                    @close="toggleSchedule"
                  ></fee-schedule>
                </mds-col>
              </mds-row>
              <mds-row v-if="showSecurityFeeSection">
                <mds-col :cols="12">
                  <mds-section
                    :class="$style['security-fees-section']"
                    border="tertiary"
                    :title="
                      settings.isActualWorkflow ? translate('investments') : translate('holdings')
                    "
                    :size="7"
                    collapsible
                    :expanded="holdingsSectionExpanded"
                  >
                    <mds-tabs v-if="showFeeTabs" :class="$style['fees-tab']">
                      <mds-tabs-item
                        v-for="item in feeTabsContent"
                        :id="item.id"
                        :key="item.id"
                        :active="item.active"
                        @click.native="setActiveFeeTab"
                      >
                        {{ item.text }}
                      </mds-tabs-item>
                    </mds-tabs>
                    <advisory-fee-table
                      v-if="showAdvisoryFeeTable"
                      :mwc-id="getMwcIdForChild('advisory-fee-table')"
                      :config="advisoryFeeTableMwcConfig"
                      :currency-symbol="options.currencySymbol"
                      :securities="advisoryFeeTableData"
                      :show-annual-asset-base-fee="options.showAnnualAssetBaseFee"
                      :show-annual-fee-frequency="options.showAnnualFeeFrequency"
                      :all-fee-schedule-list="allFeeScheduleList"
                      :asset-based-fee-config="assetBasedFeeConfig"
                      :environment="options.environment"
                      :user-info="userInfo"
                      @security-advisory-fee-changed="handleSecurityAdvisoryFeeChanged"
                      @alert-triggered="handleInvalidAlert"
                      @fee-schedules-updated="fetchLatestFeeSchedules"
                      @before-close="fetchLatestFeeSchedules"
                    ></advisory-fee-table>
                    <transaction-fee-table
                      v-if="showTransactionFeeTable"
                      :mwc-id="getMwcIdForChild('transaction-fee-table')"
                      :currency-symbol="options.currencySymbol"
                      :open-trading-commission="openTradingCommission"
                      :prop-security-table-data="securityTableData"
                      @security-transaction-fee-changed="handleSecurityTransactionFeeChanged"
                      @show-fee-schedule="showFeeSchedule"
                    >
                    </transaction-fee-table>
                  </mds-section>
                </mds-col>
              </mds-row>
            </mds-layout-grid>
          </div>
        </template>
      </component>
      <fees-summary
        v-if="options.isCollapsibleMode && !expanded"
        :mwc-id="getMwcIdForChild('fees-summary')"
        :currency-symbol="options.currencySymbol"
        :show-advisory-fee="showAdvisoryFee"
        :show-annual-fee-frequency="options.showAnnualFeeFrequency"
        :show-pay-fee-method="options.showPayFeeMethod"
        :show-transaction-fee="showTransactionFee"
        :advisory-fee-setting="advisoryFeeSetting"
        :advisory-fee-table-data="advisoryFeeTableData"
        :include-transaction-fee="transactionFeeSetting.includeTransactionFee"
        :is-equal-security-front-load="isEqualSecurityFrontLoad"
        :flag-security-with-front-load="securitiesWithFrontLoad[0]"
      ></fees-summary>
    </div>
    <div v-if="showAlertOverlay" :class="$style['alert__wrapper']">
      <div :class="$style['alert__inner-wrapper']">
        <mds-alert
          v-if="alertData !== null && showAlert"
          variation="error"
          :title="alertData.title"
          @mds-alert-dismissed="showAlert = false"
        >
          {{ alertData.text }}
        </mds-alert>
        <mds-alert
          v-if="requireAssetBaseFee !== null"
          variation="error"
          title=""
          @mds-alert-dismissed="requireAssetBaseFee = null"
        >
          {{ requireAssetBaseFee.text }}
        </mds-alert>
      </div>
    </div>
  </div>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import { MdsLayoutGrid, MdsRow, MdsCol } from '@mds/layout-grid';
import MdsLabel from '@mds/label';
import MdsMicrocopy from '@mds/microcopy';
import MdsSection from '@mds/section';
import MdsInput from '@mds/input';
import MdsSelect from '@mds/select';
import { MdsTabs, MdsTabsItem } from '@mds/tabs';
import { MdsTable, MdsThead, MdsTh, MdsTbody, MdsTr, MdsTd } from '@mds/data-table';
import MdsIcon from '@mds/icon';
import MdsAlert from '@mds/alert';
import MdsLoader from '@mds/loader';
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import defaultConfig from './_config';
import TransactionFee from './components/transaction-fee-section/TransactionFee';
import AdvisoryFee from './components/advisory-fee-section/AdvisoryFee';
import PlanFee from './components/plan-fee-section/PlanFee';
import AnnuityOptions from './components/annuity-options-section/AnnuityOptions';
import TradingCommission from './components/transaction-fee-section/TradingCommission';
import AdvisoryFeeTable from './components/advisory-fee-table/AdvisoryFeeTable';
import TransactionFeeTable from './components/transaction-fee-table/TransactionFeeTable';
import FeeSchedule from './FeeSchedule';
import FeesSummary from './FeesSummary';
import {
  getProgramFeeSchedules,
  setProgramFeeForAdvisoryFeeTableData,
  addFunFamilyDataToSecurityTableData,
  adjustFeeScheduleDetails,
  getFundFamiliesData,
  getRoaFundFamilies,
  getLoiFundFamilies,
  getFeesIncludedText,
  isEqualSecurityFrontLoad,
  getAnnuityData,
  updateVASecurityAnnuityData,
  getAnnuityOptionsData,
  getAssetBasedFeeConfig,
  getComponentProps,
  getAdvisoryFeeTableMwcConfig,
  applyRoaLoiAmountToSecurities,
  removeRoaLoiAmountFromSecurities,
  getUpdatedPortLevelIsVariousFlags,
  getUpdatedAdvisoryFeeSetting,
  updateSMASecuritiesManagerFee,
  getSMASecuritiesBaseInfo,
  getUpdatedPlanFeeSetting,
  applyFrontLoadForSecurities,
  getTransactionFeeSettingWithDataOmitted,
  getUpdatedTransactionFeeSettingWhenEqualFrontLoad,
  getDefaultTransactionFeeSettingWhenNotShow,
  updateSecuritiesIfTransactionFeeAbsent,
  transformSecuritiesTableData,
  setFeesToSecuritiesByPortLevelSettingsAndConfigs,
  applyAffectedKeysToSecurities,
  isSecurityWithTradingCommission,
  processSecurityData,
  checkAssetBasedFee,
} from './portfolio-fees-helpers';
import {
  getAdvisoryFeeSchedulesFromApi,
  getInvestmentFeeSchedulesForActual,
  getScheduleFromApi,
  getAnnuityFromApi,
} from './data-source';

export default {
  name: 'hypo-portfolio-fees',
  components: {
    MdsLayoutGrid,
    MdsRow,
    MdsCol,
    MdsLabel,
    MdsMicrocopy,
    MdsSection,
    MdsInput,
    MdsSelect,
    MdsTabs,
    MdsTabsItem,
    MdsTable,
    MdsThead,
    MdsTh,
    MdsTbody,
    MdsTr,
    MdsTd,
    MdsIcon,
    MdsAlert,
    MdsLoader,
    TransactionFee,
    AdvisoryFee,
    PlanFee,
    AnnuityOptions,
    FeeSchedule,
    TradingCommission,
    AdvisoryFeeTable,
    TransactionFeeTable,
    FeesSummary,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    mwcId: {
      type: String,
      required: true,
    },
    config: {
      type: [String, Object],
      default: () => {},
    },
    environment: String,
    userInfo: {
      type: Object,
      default: () => ({ userId: '', userLevel: 0 }), // userLevel: 0: superAdmin, 1: groupAdmin, 2: normal
    },
    currencySymbol: {
      type: String,
      default: '$',
    },
    isCollapsibleMode: {
      type: Boolean,
      default: true,
    },
    showPlanFee: {
      type: Boolean,
      default: false,
    },
    mainSectionExpanded: {
      type: Boolean,
      default: true,
    },
    planFeeSetting: Object,
    showAnnuity: {
      type: Boolean,
      default: false,
    },
    annuitySetting: {
      type: Object,
      default() {
        return { includeAnnuity: false };
      },
    },
    showAnnualAccountFee: {
      type: Boolean,
      default: true,
    },
    showAnnualAssetBaseFee: {
      type: Boolean,
      default: true,
    },
    showAnnualFeeFrequency: {
      type: Boolean,
      default: true,
    },
    showPayFeeMethod: {
      type: Boolean,
      default: true,
    },
    showPayFeeTime: {
      type: Boolean,
      default: true,
    },
    showHoldingLevelFees: {
      type: Boolean,
      default: true,
    },
    holdingsSectionExpanded: {
      type: Boolean,
      default: true,
    },
    showPayCashFirst: {
      type: Boolean,
      default: true,
    },
    showLoiAsFrontLoad: {
      type: Boolean,
      default: false,
    },
    showChargeForRebalancing: {
      type: Boolean,
      default: true,
    },
    showRoaLoi: {
      type: Boolean,
      default: true,
    },
    portfolioFees: {
      type: Object,
      default() {
        return {};
      },
    },
    securities: {
      type: Array,
    },
    isFeeSchedulesFromApi: {
      type: Boolean,
      default: true,
    },
    feeSchedules: {
      type: Array,
      default: () => [],
    },
  },
  provide() {
    return {
      rootMwcId: this.mwcId,
    };
  },
  data() {
    const {
      includeAdvisoryFee = false,
      annualAccountFeeAmount = 0,
      annualFeeAmount = 0,
      annualFeeAmountType = 1, // 0: $, 1: %
      annualFeeChargeFrequency = 1,
      annualFeePayMethod = 1,
      annualFeePayOnBegin = false,
      annualFeePayCashFirst = true,
      includeTransactionFee = false,
      chargeOnRebalancing = true,
      annualFeeType = 0, // 0: maxFee or feeSchedule or maxFeeAndFeeSchedule, 1: flat Amount ($ or %), 99: vah
      feeScheduleId = '',
    } = this.portfolioFees || {};

    const config = {
      intlNamespace: this.mwcId,
      ...defaultConfig,
      format: {
        longDecimal: {
          dataType: 'number',
          minimumFractionDigits: 0,
          maximumFractionDigits: 4,
        },
      },
    };
    config.settings.languageId =
      this.getComponentConfig()?.settings?.languageId ||
      this.config?.settings?.languageId ||
      'en-US';

    return {
      defaultConfig: config,
      showSchedule: false,
      feeSchedule: [],
      selectedSchedule: [],
      scheduleSecurities: [],
      loadingSchedule: false,
      expanded: this.mainSectionExpanded,
      advisoryFeeSetting: {
        includeAdvisoryFee,
        annualAccountFeeAmount,
        annualFeeAmount,
        annualFeeAmountType,
        annualFeeChargeFrequency,
        annualFeePayMethod,
        annualFeePayOnBegin,
        annualFeePayCashFirst,
        annualFeeType,
        feeScheduleId,
      },
      transactionFeeSetting: {
        includeTransactionFee,
        chargeOnRebalancing,
      },
      selectPlanFeeSetting: this.planFeeSetting,
      advisoryFeeTableData: cloneDeep(this.securities),
      securityTableData: [],
      openTradingCommission: false,
      activeFeeTabId: 'advisory',
      annuitySecIdARecord: {},
      allFeeScheduleList: [],
      alertData: null,
      showAlert: false,
      requireAssetBaseFee: null,
      loading: false,
      fundFamiliesApiData: [],
      vaSecuritiesPolices: [],
      smaSecuritiesBaseInfo: [],
      privateModelSecurities: [],
    };
  },
  computed: {
    defaultVals() {
      return this.settings?.preference;
    },
    componentType() {
      return this.options.isCollapsibleMode
        ? MdsSection
        : {
            render(h) {
              return h('div', [this.$scopedSlots.default()]);
            },
          }; // use render function
    },
    componentProps() {
      return getComponentProps(this.options.isCollapsibleMode, this.expanded);
    },
    showSecurityLevelAdvisoryFee() {
      return this.advisoryFeeSetting.includeAdvisoryFee && this.showAdvisoryFee;
    },
    showSecurityLevelTransactionFee() {
      return this.transactionFeeSetting.includeTransactionFee && this.showTransactionFee;
    },
    showSecurityFeeSection() {
      return (
        this.showHoldingLevelFees &&
        (this.showSecurityLevelAdvisoryFee || this.showSecurityLevelTransactionFee)
      );
    },
    showFeeTabs() {
      return (
        this.showHoldingLevelFees &&
        this.showSecurityLevelAdvisoryFee &&
        this.showSecurityLevelTransactionFee
      );
    },
    isAdvisoryFeeTableActive() {
      return this.activeFeeTabId === 'advisory';
    },
    isTransactionFeeTableActive() {
      return this.activeFeeTabId === 'transaction';
    },
    feeTabsContent() {
      return [
        {
          text: this.translate('advisoryFees'),
          id: 'advisory',
          active: this.isAdvisoryFeeTableActive,
        },
        {
          text: this.translate('transactionFees'),
          id: 'transaction',
          active: this.isTransactionFeeTableActive,
        },
      ];
    },
    showAdvisoryFeeTable() {
      if (this.showFeeTabs) {
        return this.isAdvisoryFeeTableActive;
      }
      return this.showSecurityLevelAdvisoryFee;
    },
    showTransactionFeeTable() {
      if (this.showFeeTabs) {
        return this.isTransactionFeeTableActive;
      }
      return this.showSecurityLevelTransactionFee;
    },
    feesIncludedText() {
      return getFeesIncludedText(
        this.advisoryFeeSetting.includeAdvisoryFee,
        this.transactionFeeSetting.includeTransactionFee,
        this.showAdvisoryFee,
        this.showTransactionFee,
        this.options.showPlanFee,
        this.selectPlanFeeSetting?.includePlanFee,
        this
      );
    },
    isEqualSecurityFrontLoad() {
      return isEqualSecurityFrontLoad(this.securitiesWithFrontLoad);
    },
    securitiesWithFrontLoad() {
      return this.securityTableData.filter(sec => !isSecurityWithTradingCommission(sec));
    },
    securitiesWithTC() {
      return this.securityTableData.filter(sec => isSecurityWithTradingCommission(sec));
    },
    roaLoiData() {
      return {
        roa: {
          fundFamilies: getRoaFundFamilies(this.securityTableData),
        },
        loi: {
          fundFamilies: getLoiFundFamilies(this.securityTableData),
        },
      };
    },
    hasVASecurities() {
      return this.securityTableData.some(sec => sec.securityType === 'VA');
    },
    assetBasedFeeConfig() {
      return getAssetBasedFeeConfig(
        this.settings?.assetBasedFeeConfig,
        this.userInfo.userLevel,
        this.allFeeScheduleList
      );
    },
    showAlertOverlay() {
      // show overlay container for any alert.
      return (this.alertData !== null && this.showAlert) || this.requireAssetBaseFee !== null;
    },
    showAdvisoryFee() {
      return this.settings?.showAdvisoryFee;
    },
    showTransactionFee() {
      return this.settings?.showTransactionFee;
    },
    advisoryFeeTableMwcConfig() {
      return getAdvisoryFeeTableMwcConfig(this.settings);
    },
    annuityOptionsData() {
      return getAnnuityOptionsData(this.securityTableData, this.vaSecuritiesPolices);
    },
  },
  watch: {
    securityTableData: {
      handler() {
        this.updateAndEmitTransactionFeeSetting();
        this.emitSecuritiesData();
      },
      deep: true,
    },
    planFeeSetting: {
      handler() {
        this.selectPlanFeeSetting = this.planFeeSetting;
      },
    },
    securities: {
      handler(newValue, oldValue) {
        if (!isEqual(newValue, oldValue)) {
          this.initData();
        }
      },
      deep: true,
    },
    allFeeScheduleList() {
      this.$emit('fee-schedules-updated', this.allFeeScheduleList);
    },
  },
  created() {
    if (typeof this.environment !== 'undefined') {
      this.options.environment = this.environment;
    }
    this.initData();
  },
  methods: {
    async loadApiData() {
      try {
        this.loading = true;
        await this.getROALOIData();
        await this.getSchedule();
        await this.getAnnuity();
        await this.getAllFeeScheduleList();
        this.smaSecuritiesBaseInfo = await getSMASecuritiesBaseInfo(
          this.securities,
          this.settings.languageId,
          this.options.environment
        );
      } catch (e) {
        this.logger.error('initialized API request failed.');
      } finally {
        this.loading = false;
      }
    },
    async initData() {
      this.lastUpdatedSecuritiesData = cloneDeep(this.securities);
      await this.loadApiData();
      this.initAdvisoryFeeTableData();
      this.initSecurityTableData();
      this.emitSecuritiesData();
    },
    initSecurityTableData() {
      let securities = updateSecuritiesIfTransactionFeeAbsent(
        this.transactionFeeSetting,
        cloneDeep(this.securities),
        this.defaultVals,
        this.showRoaLoi,
        this.showLoiAsFrontLoad
      );
      securities = transformSecuritiesTableData(securities);
      securities = this.updateSecurityRoaLoiFundFamilyData(securities);
      this.recordAnnuitySecId();
      securities = updateVASecurityAnnuityData(
        this.annuityOptionsData,
        this.vaSecuritiesPolices,
        securities
      );
      this.securityTableData = securities;
      this.updateAndEmitTransactionFeeSetting();
    },
    initAdvisoryFeeTableData() {
      let securities = cloneDeep(this.securities);
      if (this.isFeeSchedulesFromApi && this.settings.isActualWorkflow) {
        securities = setProgramFeeForAdvisoryFeeTableData(
          securities,
          this.allFeeScheduleList,
          this.privateModelSecurities
        );
      } else {
        securities = processSecurityData(
          securities,
          this.assetBasedFeeConfig,
          this.allFeeScheduleList,
          this.defaultVals
        );
        securities = updateSMASecuritiesManagerFee(this.smaSecuritiesBaseInfo, securities);
      }
      this.advisoryFeeTableData = securities;
      this.updateAdvisoryFeeSettingsAccordingToSecurities();
    },
    updateSecurityRoaLoiFundFamilyData(securities) {
      return addFunFamilyDataToSecurityTableData(this.fundFamiliesApiData, securities);
    },
    // for recovering data when include annuity switch on
    recordAnnuitySecId() {
      const record = this.annuitySecIdARecord;
      this.vaSecuritiesPolices.forEach(({ policyId, secId }) => {
        if (!record[policyId]) {
          record[policyId] = [secId];
        } else {
          record[policyId].push(secId);
        }
      });
    },
    emitSecuritiesData() {
      const securityFeesData = setFeesToSecuritiesByPortLevelSettingsAndConfigs(this);
      if (!isEqual(securityFeesData, this.lastUpdatedSecuritiesData)) {
        this.$emit('security-fees-changed', securityFeesData);
        this.lastUpdatedSecuritiesData = securityFeesData;
      }
    },
    updateAdvisoryFeeSettingsAccordingToSecurities() {
      if (!Array.isArray(this.advisoryFeeTableData) || this.advisoryFeeTableData.length === 0) {
        return;
      }
      this.advisoryFeeSetting = getUpdatedAdvisoryFeeSetting(
        this.advisoryFeeSetting,
        this.advisoryFeeTableData,
        this.allFeeScheduleList
      );
      this.$emit('advisory-fee-changed', this.advisoryFeeSetting);
    },
    // Fee Schedule Methods
    async getSchedule() {
      if (!this.showTransactionFee) return;
      this.loadingSchedule = true;
      this.feeSchedule = await getScheduleFromApi(
        this.securities,
        this.settings.tokenSource,
        this.options.environment
      );
      this.loadingSchedule = false;
    },
    async getAllFeeScheduleList() {
      if (this.isFeeSchedulesFromApi) {
        if (this.settings.isActualWorkflow) {
          const privateModelSecurities = await getInvestmentFeeSchedulesForActual(
            this.securities.map(security => security.securityId),
            this.options.environment
          );
          const programFeeSchedules = await getProgramFeeSchedules(privateModelSecurities);
          this.allFeeScheduleList = programFeeSchedules;
          this.privateModelSecurities = privateModelSecurities;
        } else {
          this.allFeeScheduleList = await this.getAllFeeScheduleListByApi();
        }
      } else {
        this.allFeeScheduleList = this.feeSchedules;
      }
    },
    async getAllFeeScheduleListByApi() {
      const data = await getAdvisoryFeeSchedulesFromApi(
        this.userInfo.userId,
        this.settings.isExternalUser,
        this.options.environment
      );
      return data.map(feeSchedule => adjustFeeScheduleDetails(feeSchedule));
    },
    toggleSchedule() {
      this.showSchedule = !this.showSchedule;
    },
    showFeeSchedule(sec) {
      this.scheduleSecurities = [sec];
      this.selectedSchedule = this.feeSchedule.filter(item => item.secId === sec.securityId);
      this.toggleSchedule();
    },
    showStandardFeeSchedule() {
      this.scheduleSecurities = cloneDeep(this.securities);
      this.selectedSchedule = cloneDeep(this.feeSchedule);
      this.toggleSchedule();
    },
    // ROALOI methods
    async getROALOIData() {
      if (this.showTransactionFee && this.showRoaLoi) {
        this.fundFamiliesApiData = await getFundFamiliesData(
          this.securities,
          this.settings.tokenSource,
          this.options.environment,
          this.settings.languageId
        );
      }
    },
    handleRoaLoiFeeChanged($event) {
      this.securityTableData = applyRoaLoiAmountToSecurities($event, this.securityTableData);
    },
    handleRoaLoiFeeRemoved() {
      this.securityTableData = removeRoaLoiAmountFromSecurities(this.securityTableData);
    },
    // Annuity methods
    async getAnnuity() {
      if (this.options.showAnnuity) {
        this.vaSecuritiesPolices = await getAnnuityFromApi(
          this.securities,
          this.settings.tokenSource,
          this.options.environment
        );
      }
    },
    applyAnnuityOptionsDataToSecurityTable(newAnnuityData) {
      newAnnuityData.forEach(annuity => {
        const { policyId } = annuity;
        const secList = this.annuitySecIdARecord[policyId];
        secList.forEach(secId => {
          const targetSecIndex = this.securityTableData.findIndex(sec => {
            return sec.securityId === secId;
          });
          this.securityTableData[targetSecIndex].annuity = annuity;
        });
      });
      this.emitSecuritiesData();
    },
    handleAnnuityOptionsSwitchOn(newAnnuityData) {
      if (getAnnuityData(this.vaSecuritiesPolices).length === 0) {
        // first time switch on
        this.getAnnuity();
      } else {
        this.applyAnnuityOptionsDataToSecurityTable(newAnnuityData);
      }
    },
    clearAnnuityInSecurityTable() {
      // avoid clearing saved data when first time switch on
      if (getAnnuityData(this.vaSecuritiesPolices).length !== 0) {
        this.securityTableData.forEach(sec => {
          this.$delete(sec, 'annuity');
        });
      }
    },
    setActiveFeeTab(e) {
      this.activeFeeTabId = e.currentTarget.id;
    },
    handleAdvisoryFeeSettingChanged(newAdvisoryFeeSetting) {
      // make sure logic in AdvisoryFee component is right and newAdvisoryFeeSetting emitted is already correct
      if (!this.showHoldingLevelFees) {
        this.advisoryFeeSetting = newAdvisoryFeeSetting;
      } else {
        this.advisoryFeeTableData = applyAffectedKeysToSecurities(
          newAdvisoryFeeSetting,
          this.advisoryFeeTableData,
          this.allFeeScheduleList
        );
        if (newAdvisoryFeeSetting.annualFeeType !== this.advisoryFeeSetting.annualFeeType) {
          this.handleInvalidAlert(null);
          this.resetAdvisorySecuritiesValidity();
        }
        this.advisoryFeeSetting = {
          ...newAdvisoryFeeSetting,
          ...getUpdatedPortLevelIsVariousFlags(this.advisoryFeeTableData),
        };
      }
      this.$emit('advisory-fee-changed', this.advisoryFeeSetting);
    },
    resetAdvisorySecuritiesValidity() {
      this.advisoryFeeTableData.forEach(sec => {
        this.$delete(sec, 'validity');
      });
    },
    handleSecurityAdvisoryFeeChanged(advisoryFeeSecurities) {
      this.advisoryFeeTableData = advisoryFeeSecurities;
      this.updateAdvisoryFeeSettingsAccordingToSecurities();
      this.emitSecuritiesData();
    },
    handleSecurityTransactionFeeChanged(securityTableData) {
      this.securityTableData = securityTableData;
    },
    handlePlanFeeChanged(planFeeData) {
      this.selectPlanFeeSetting = {
        ...this.selectPlanFeeSetting,
        ...getUpdatedPlanFeeSetting(planFeeData),
      };
      this.$emit('plan-fee-changed', planFeeData);
    },
    toggleTransactionFee(includeTransactionFee) {
      this.transactionFeeSetting = {
        ...this.transactionFeeSetting,
        includeTransactionFee,
      };
      this.$emit('transaction-fee-changed', this.transactionFeeSetting);
      this.emitSecuritiesData();
    },
    handleFrontLoadChanged(frontLoadFeeData) {
      this.securityTableData = applyFrontLoadForSecurities(
        frontLoadFeeData,
        this.securityTableData
      );
    },
    applyRedemptionChargeForSecurities(redemptionCharge) {
      this.securityTableData = this.securityTableData.map(security => {
        return {
          ...security,
          redemptionCharge,
        };
      });
    },
    applyChargeOnRebalancing(chargeOnRebalancing) {
      this.transactionFeeSetting.chargeOnRebalancing = chargeOnRebalancing;
      this.$emit('transaction-fee-changed', this.transactionFeeSetting);
    },
    updateAndEmitTransactionFeeSetting() {
      const transactionFeeSetting = {
        ...getTransactionFeeSettingWithDataOmitted(
          this.transactionFeeSetting,
          this.securitiesWithFrontLoad
        ),
        ...getUpdatedTransactionFeeSettingWhenEqualFrontLoad(this.securitiesWithFrontLoad),
        ...getDefaultTransactionFeeSettingWhenNotShow(
          this.showTransactionFee,
          this.transactionFeeSetting,
          this.defaultVals
        ),
      };
      if (!isEqual(this.transactionFeeSetting, transactionFeeSetting)) {
        this.transactionFeeSetting = transactionFeeSetting;
        this.$emit('transaction-fee-changed', this.transactionFeeSetting);
      }
    },
    handleToggleTradingCommission(bool) {
      this.openTradingCommission = bool;
      if (!bool) {
        this.securityTableData.forEach((sec, index) => {
          if (isSecurityWithTradingCommission(sec)) {
            sec.tradingCommission = cloneDeep(this.securities[index].tradingCommission);
          }
        });
      }
    },
    applyChangeToAllSecWithTC(data) {
      this.securityTableData.forEach(sec => {
        if (isSecurityWithTradingCommission(sec)) {
          sec.tradingCommission = cloneDeep(data);
        }
      });
    },
    handleInvalidAlert(alertData) {
      if (alertData) {
        this.alertData = alertData;
        this.showAlert = true;
        setTimeout(() => {
          this.showAlert = false;
        }, 3000);
      } else {
        this.showAlert = false;
        this.alertData = null;
      }
    },
    checkAssetBasedFee() {
      return checkAssetBasedFee(this);
    },
    async fetchLatestFeeSchedules(cb) {
      if (!this.settings.isActualWorkflow) {
        await this.getAllFeeScheduleList();
      }
      if (typeof cb === 'function') {
        cb();
      }
    },
  },
};
</script>

<style lang="scss" module>
@import '~@mds/constants';
@import '~@mds/typography';
@import '~@mds/link';
@import './styles/variables';

.#{$component-ns} {
  background-color: $mds-background-color-white;
  position: relative;
}

.alert {
  &__wrapper {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1;
  }
  &__inner-wrapper {
    background-color: $mds-color-white;
    width: 60%;
    margin: 0 auto;
    margin-top: $mds-space-5-x;

    .mds-button.mds-button--icon-only.mds-alert__dismiss-button {
      position: absolute;
    }
  }
}

.fees-tab {
  margin-bottom: $mds-space-2-x;
}

.fees-section {
  padding: 0 $mds-space-2-x;
}

.fee-included-text {
  @include mds-body-text-s();
}

.sub-section {
  margin-top: $mds-space-1-and-a-half-x;
}

.security-fees-section {
  margin-top: $mds-space-2-x;
}

.fee-link {
  @include mds-link($underline: false);
  cursor: pointer;
  display: inline-block;
  margin-top: $mds-space-1-x;
}
</style>
